import React, { useMemo, useState } from "react";

import ChartCard from "./ChartCard";
import ChartModeToggle from "./ChartModeToggle";
import TimelineBarChart from "./TimelineBarChart";
import TimelineLineChart from "./TimelineLineChart";
import useTimelineChart from "./useTimelineChart";

import type { Moment } from "moment";
import type { JSX, ReactNode } from "react";

import type { Query } from "@/src/api/BaseAPI";

import type {
  ChartMode,
  ChartSeries,
  ChartValueUnit,
  TimelineDatum,
  TimelineKind,
} from "./charts.types";

interface TimelineQueryCardProps<TResponse, TKey extends string> {
  title: string;
  query: Query<TResponse>;
  /** Must keep a stable identity (module-level function) to preserve the memo. */
  buildTimeline: (response: TResponse | undefined) => TimelineDatum<TKey>[];
  seriesList: readonly ChartSeries<TKey>[];
  startDate: Moment;
  endDate: Moment;
  kind?: TimelineKind;
  unit?: ChartValueUnit;
  extraActions?: ReactNode;
  isNotFilterableByOffer?: boolean;
}

function TimelineQueryCard<TResponse, TKey extends string>({
  title,
  query,
  buildTimeline,
  seriesList,
  startDate,
  endDate,
  kind = "flow",
  unit,
  extraActions,
  isNotFilterableByOffer,
}: TimelineQueryCardProps<TResponse, TKey>): JSX.Element {
  const [mode, setMode] = useState<ChartMode>("periodic");
  const keyList = useMemo(
    () => seriesList.map((series) => series.key),
    [seriesList],
  );
  const { timelineList, granularity, status, isFetching } = useTimelineChart({
    query,
    buildTimeline,
    keyList,
    startDate,
    endDate,
    kind,
  });
  const isFlow = kind === "flow";

  return (
    <ChartCard
      isFetching={isFetching}
      isNotFilterableByOffer={isNotFilterableByOffer}
      status={status}
      title={title}
      actions={
        <React.Fragment>
          {isFlow ? (
            <ChartModeToggle
              granularity={granularity}
              onChange={setMode}
              value={mode}
            />
          ) : null}
          {extraActions}
        </React.Fragment>
      }
    >
      {isFlow ? (
        <TimelineBarChart
          dataList={timelineList}
          granularity={granularity}
          mode={mode}
          seriesList={seriesList}
          unit={unit}
        />
      ) : (
        <TimelineLineChart
          dataList={timelineList}
          granularity={granularity}
          seriesList={seriesList}
          unit={unit}
        />
      )}
    </ChartCard>
  );
}

export default TimelineQueryCard;
